import { AccordionContent } from "@/components/ui/library/accordion";
import { ChildDocUpload } from "../../../../../components/components/ChildDocUpload";
import { ChildDocumentCard } from "@/module/components/components";
import { useEduChildModuleData } from "@/module/education/helpers/useEduChildModuleData";
import usePipeline from "@/features/stage/api/usePipeline";
import { filterSequentialSubStatuses } from "@/features/leads/utils/sequentialSubStatus";
import { useStatusRbac } from "@/rbac/useStatusRbac";
import { useChildMutations } from "@/features/leads/api/useChildMutations";
import { refetchChilds } from "@/features/leads/api/useChilds";
import { refetchCurrentChild } from "@/features/leads/api/useCurrentChild";
import { refetchCurrentLead } from "@/features/leads/api/useCurrentLead";
import { useActiveDataMutation } from "@/features/leads/hook/useActiveDataMutation";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/library/select";
import {
  Avatar,
  AvatarFallback,
  AvatarImage,
} from "@/components/ui/library/avatar";
import { Button } from "@/components/ui/button";
import { getAvatarUrl } from "@/config/endpoints";
import { useState, useMemo, useEffect, useCallback } from "react";
import { useDispatch } from "react-redux";
import {
  setCurrentDetailChild,
  setIsStageViewOverride,
} from "@/features/leads/stores/childSlice";
import {
  GraduationCap,
  Building2,
  ArrowUpRight,
  Loader2,
  LinkIcon,
} from "lucide-react";
import { toast } from "sonner";
import AddCourseButton from "@/features/universityAndCourse/components/add-edit-Course/AddCourseButton";
import AttachCourseModal from "@/features/leads/components/view-lead/components/Ongoing/components/AttachCourseModal";
import { dealStageIdOf, useCanEditDeal } from "@/rbac/useCanEditDeal";

const fmt = (v: number) =>
  v.toLocaleString("en-IN", { maximumFractionDigits: 0 });

export function ChildContent({ child }: any) {
  if (!child) return null;

  const dispatch = useDispatch();
  const { universityName, courseName } = useEduChildModuleData(child);
  const hasCourse = !!(universityName || courseName);

  const { pipeline } = usePipeline();
  const { updateStage } = useChildMutations();
  const { lead } = useActiveDataMutation();
  // Attaching a course to this existing deal is a deal edit — "Edit
  // choices" on the deal's own stage.
  const canEditDeal = useCanEditDeal();
  const canEditThisDeal = canEditDeal(dealStageIdOf(child));

  const [attachModalOpen, setAttachModalOpen] = useState(false);

  const serverStatusId = child?.status?.id
    ? String(child.status.id)
    : undefined;
  const serverSubStatusId = child?.subStatus?.id
    ? String(child.subStatus.id)
    : undefined;
  const serverStageId = child?.stage?.id ? String(child.stage.id) : undefined;

  const [statusId, setStatusId] = useState<string | undefined>(serverStatusId);
  const [subStatusId, setSubStatusId] = useState<string | undefined>(
    serverSubStatusId
  );

  useEffect(() => {
    setStatusId(serverStatusId);
    setSubStatusId(serverSubStatusId);
  }, [serverStatusId, serverSubStatusId]);

  const currentStage = useMemo(
    () => pipeline?.find((s: any) => String(s.id) === serverStageId),
    [pipeline, serverStageId]
  );

  // Role-denied statuses (wizard "Status" block) are hidden from the
  // picker; the deal's server/draft status is pinned so a deal already
  // sitting in a denied status still displays it.
  const { filterRoleAllowedStatuses } = useStatusRbac();
  const statuses = useMemo(
    () =>
      filterRoleAllowedStatuses(
        currentStage?.id,
        currentStage?.statuses ?? currentStage?.statusList ?? [],
        [serverStatusId, statusId]
      ),
    [currentStage, filterRoleAllowedStatuses, serverStatusId, statusId]
  );

  const selectedStatus = useMemo(
    () => statuses.find((st: any) => String(st.id) === statusId),
    [statuses, statusId]
  );

  const subStatuses = useMemo(
    () => selectedStatus?.subStatuses ?? [],
    [selectedStatus]
  );

  // "Sequence wise" statuses offer one sub-status step at a time (see
  // filterSequentialSubStatuses); the full list still backs the save fallback.
  const subStatusOptions = useMemo(
    () =>
      filterSequentialSubStatuses(subStatuses, selectedStatus?.isSequential, {
        entering: statusId !== serverStatusId,
        currentSubStatusId: serverSubStatusId,
        pinnedIds: [serverSubStatusId, subStatusId],
      }),
    [subStatuses, selectedStatus, statusId, serverStatusId, serverSubStatusId, subStatusId]
  );

  const isDirty =
    statusId !== serverStatusId || subStatusId !== serverSubStatusId;

  const handleUpdateStatus = useCallback(async () => {
    if (!child?.id || !statusId || !serverStageId) return;
    const resolvedSubStatusId = subStatusId || subStatuses[0]?.id;
    if (!resolvedSubStatusId) {
      toast.error("Please select a valid sub-status.");
      return;
    }
    try {
      await updateStage.mutateAsync({
        id: child.id,
        data: {
          stageId: Number(serverStageId),
          statusId: Number(statusId),
          subStatusId: Number(resolvedSubStatusId),
        },
      });
      await Promise.all([
        refetchChilds(),
        refetchCurrentChild(),
        refetchCurrentLead(),
      ]);
    } catch {
      // handled by mutation
    }
  }, [
    child?.id,
    statusId,
    subStatusId,
    serverStageId,
    subStatuses,
    updateStage,
  ]);

  const handleAttachClose = () => {
    setAttachModalOpen(false);
    Promise.all([refetchChilds(), refetchCurrentChild(), refetchCurrentLead()]);
  };

  const handleViewMore = () => {
    dispatch(setIsStageViewOverride(true));
    dispatch(setCurrentDetailChild(child));
  };

  const childUsers = (child?.childUsers ?? [])
    .map((u: any) => u?.user)
    .filter(Boolean);
  const displayUser =
    childUsers[0] ??
    lead?.leadUsers?.find((u: any) => u.isLeadOwner)?.user ??
    lead?.leadUsers?.[0]?.user ??
    null;

  const stageName = currentStage?.name || child?.stage?.name || "General";

  const dealValue = child.totalDealValue || 0;
  const discountAmount = child.discountAmount || 0;
  const addonAmount = child.addonAmount || 0;
  const netAmount = child.netAmount || dealValue;
  const hasFinancials = dealValue > 0 || discountAmount > 0 || addonAmount > 0;

  return (
    <AccordionContent className="pt-2.5 pb-0">
      {/* Status / Sub-status selectors */}
      <div className="flex items-center gap-1.5 mb-2">
        <Select
          value={statusId}
          onValueChange={(val) => {
            setStatusId(val);
            const newStatus = statuses.find((st: any) => String(st.id) === val);
            const firstSub = newStatus?.subStatuses?.[0];
            setSubStatusId(firstSub ? String(firstSub.id) : undefined);
          }}
        >
          <SelectTrigger className="h-7 rounded-lg border-[#d4d8d6] bg-white text-[12px] px-2.5 w-auto min-w-0 flex-1">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            {statuses.map((st: any) => (
              <SelectItem key={st.id} value={String(st.id)}>
                {st.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        {subStatuses.length > 0 && (
          <Select value={subStatusId} onValueChange={setSubStatusId}>
            <SelectTrigger className="h-7 rounded-lg border-[#d4d8d6] bg-white text-[12px] px-2.5 w-auto min-w-0 flex-1">
              <SelectValue placeholder="Sub-status" />
            </SelectTrigger>
            <SelectContent>
              {subStatusOptions.map((ss: any) => (
                <SelectItem key={ss.id} value={String(ss.id)}>
                  {ss.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}

        {isDirty && (
          <Button
            variant="primary"
            size="sm"
            onClick={(e) => {
              e.stopPropagation();
              handleUpdateStatus();
            }}
            disabled={updateStage.isPending}
            className="h-7 px-3 rounded-lg text-[11px] font-semibold shrink-0"
          >
            {updateStage.isPending ? (
              <Loader2 className="size-3 animate-spin" />
            ) : (
              "Update"
            )}
          </Button>
        )}
      </div>

      {/* Course info or Attach Course */}
      {hasCourse ? (
        <div className="flex items-center gap-3 mb-2 px-2.5 py-1.5 rounded-lg bg-[#f7f9f8] border border-[#e6e8e7]">
          {universityName && (
            <span className="inline-flex items-center gap-1 text-[12px] text-[#697571]">
              <Building2 className="size-3 shrink-0 text-[#9ba3a0]" />
              <span className="truncate max-w-[150px]">{universityName}</span>
            </span>
          )}
          {universityName && courseName && (
            <span className="text-[#c5c9c7]">·</span>
          )}
          {courseName && (
            <span className="inline-flex items-center gap-1 text-[12px] text-[#697571]">
              <GraduationCap className="size-3 shrink-0 text-[#9ba3a0]" />
              <span className="truncate max-w-[150px]">{courseName}</span>
            </span>
          )}
        </div>
      ) : canEditThisDeal ? (
        <button
          onClick={(e) => {
            e.stopPropagation();
            setAttachModalOpen(true);
          }}
          className="w-full mb-2 px-3 py-2 rounded-lg border border-dashed border-[#c5c9c7] bg-[#fafbfa] hover:bg-[#f0f2f1] flex items-center justify-center gap-1.5 text-[12px] text-[#697571] transition-colors"
        >
          <LinkIcon className="size-3" />
          Attach Course
        </button>
      ) : null}
      {!hasCourse && canEditThisDeal && (
        <AddCourseButton className="w-full mb-2 px-3 py-2 rounded-lg border border-dashed border-[#c5c9c7] bg-[#fafbfa] hover:bg-[#f0f2f1] flex items-center justify-center gap-1.5 text-[12px] text-[#697571] transition-colors" />
      )}

      {/* Financial metrics */}
      {hasFinancials && (
        <div className="grid grid-cols-3 mb-2 rounded-lg overflow-hidden border border-[#e6e8e7] bg-[#fafbfb]">
          <FinMetric label="Deal Value" value={`₹${fmt(dealValue)}`} />
          <FinMetric
            label="Discount"
            value={discountAmount > 0 ? `-₹${fmt(discountAmount)}` : "—"}
          />
          <FinMetric label="Net" value={`₹${fmt(netAmount)}`} accent />
        </div>
      )}

      {/* Documents */}
      {(!child.isFroze || child?.documents?.length > 0) && (
        <div className="flex flex-col gap-1.5 mb-2">
          {!child.isFroze && (
            <ChildDocUpload
              childId={child.id}
              stageName={stageName}
              childName={child.name}
            />
          )}
          {child?.documents?.length > 0 && (
            <div className="flex gap-1.5 items-start flex-wrap">
              {child.documents.map((doc: any) => (
                <ChildDocumentCard key={doc.id} document={doc} />
              ))}
            </div>
          )}
        </div>
      )}

      {/* Footer: Owner + View More */}
      <div className="flex items-center justify-between pt-2 border-t border-[#f0f2f1]">
        {displayUser ? (
          <div className="flex gap-1.5 items-center">
            <Avatar className="size-5 shrink-0">
              <AvatarImage src={getAvatarUrl(displayUser?.name || "NA")} />
              <AvatarFallback className="text-[8px] font-medium bg-[#351A75] text-white">
                {(displayUser?.name || "NA").slice(0, 2).toUpperCase()}
              </AvatarFallback>
            </Avatar>
            <span className="text-[11px] text-[#697571]">
              {displayUser?.name}
            </span>
          </div>
        ) : (
          <span className="text-[11px] text-[#9ba3a0]">Unassigned</span>
        )}

        <button
          onClick={(e) => {
            e.stopPropagation();
            handleViewMore();
          }}
          className="inline-flex items-center gap-1 text-[11px] font-medium text-[#267355] hover:text-[#1e5c44] transition-colors"
        >
          View more
          <ArrowUpRight className="size-3" />
        </button>
      </div>

      {/* Attach Course Modal */}
      <AttachCourseModal
        open={attachModalOpen}
        onClose={handleAttachClose}
        dealId={child.id}
        dealName={child.name || "Deal"}
      />
    </AccordionContent>
  );
}

function FinMetric({
  label,
  value,
  accent,
}: {
  label: string;
  value: string;
  accent?: boolean;
}) {
  return (
    <div className="flex flex-col items-center justify-center py-1.5 border-r border-[#e6e8e7] last:border-r-0">
      <span className="text-[9px] font-semibold text-[#9ba3a0] uppercase tracking-[0.3px] leading-none">
        {label}
      </span>
      <span
        className={`text-[12px] font-semibold tabular-nums leading-tight mt-0.5 ${accent ? "text-[#267355]" : "text-[#1e302a]"}`}
      >
        {value}
      </span>
    </div>
  );
}
